import { Button } from 'antd';
import produce from 'immer';
import React, { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { DeleteOutlined } from '@ant-design/icons';

import convertDayRangeToTimeRange from '../../../utils/timeRangeMagic';
import { Carousel, FormWrapper, Text } from '../../../WidgetConfiguratorComponents/FormGroups';
import DatabilityItem from './DatabilityItem';

const DatabilitiesConfigurator = ({ viewblockState, setViewblockState, baseDatability }) => {
    const defaultDecimalPoint = viewblockState.settings.dp || 2;

    const onHeadingChange = (index, key) => (value) => {
        setViewblockState(
            produce((prev) => {
                prev.settings.headings[index][key] = value;
            })
        );
    };

    const onAddDataGroup = useCallback(() => {
        setViewblockState(
            produce((prev) => {
                prev.settings.headings.push({
                    id: uuid(),
                    label: 'New group',
                    databilities: [],
                    databilityLabels: [],
                    databilityUnits: [],
                    databilityUnitLabels: [],
                    aggregateFunctions: [],
                    colours: [],
                    vectorNormalization: [],
                    dp: [],
                    directions: [],
                    directionalVisualization: [],
                });
            })
        );
    }, [setViewblockState]);

    const onDeleteDataGroup = (id) => {
        setViewblockState(
            produce((prev) => {
                const heading = prev.settings.headings.find((heading) => id === heading.id);
                const databilities = heading.databilities;

                prev.settings.headings = prev.settings.headings.filter((heading) => id !== heading.id);
                prev.settings.limits = prev.settings.limits.filter(({ itemId }) => itemId.indexOf(id) !== 0);
                prev.databilities = prev.databilities.filter((datability) => databilities.indexOf(datability.id) === -1);
            })
        );
    };

    const onAddHeading = (headingIndex) => {
        setViewblockState(
            produce((prev) => {
                const dayRangeMin = prev.settings.dayRange.min - 1;
                const dayRangeMax = prev.settings.dayRange.max + 1;
                const timeRange = convertDayRangeToTimeRange({ min: dayRangeMin, max: dayRangeMax });
                const databilityId = uuid();

                prev.settings.headings.forEach((heading, index) => {
                    if (index === headingIndex) {
                        heading.dp = heading.dp
                            ? heading.dp.concat(defaultDecimalPoint)
                            : heading.colours.map(() => defaultDecimalPoint).concat(defaultDecimalPoint);
                        heading.databilities.push(databilityId);
                        heading.databilityLabels.push('');
                        heading.databilityUnits.push('m');
                        heading.databilityUnitLabels.push('m');
                        heading.aggregateFunctions.push('median');
                        heading.colours.push('#000000');
                        heading.vectorNormalization.push('magnitude');
                        heading.directions.push(undefined);
                        heading.directionalVisualization.push('original');
                    } else {
                        heading.dp = heading.dp
                            ? heading.dp.concat(defaultDecimalPoint)
                            : heading.colours.map(() => defaultDecimalPoint).concat(defaultDecimalPoint);
                    }
                });

                prev.databilities.push({
                    ...baseDatability,
                    id: databilityId,
                    capabilityId: null,
                    timeRange,
                    datasetId: null,
                });
            })
        );
    };

    const onDeleteHeading = (headingIndex, databilityIndex) => {
        setViewblockState(
            produce((prev) => {
                const heading = prev.settings.headings[headingIndex];
                const databilityId = heading.databilities[databilityIndex];
                const limitId = `${heading.id}-${databilityIndex}`;

                prev.settings.headings = prev.settings.headings.map((heading, stateHeadingIndex) => {
                    if (headingIndex === stateHeadingIndex) {
                        const updated = [
                            'databilities',
                            'databilityLabels',
                            'databilityUnits',
                            'databilityUnitLabels',
                            'aggregateFunctions',
                            'colours',
                            'vectorNormalization',
                            'dp',
                        ].reduce((acc, key) => {
                            if (heading[key]) {
                                acc[key] = heading[key].filter((_v, i) => i !== databilityIndex);
                            }

                            return acc;
                        }, {});

                        return {
                            ...heading,
                            ...updated,
                        };
                    }

                    return heading;
                });

                prev.settings.limits = prev.settings.limits
                    .filter((limit) => limit.itemId.indexOf(limitId) !== 0) // Remove limit entities that reference this data
                    .map((limit) => {
                        // Re-index limits
                        const itemIdArray = limit.itemId.split('-');

                        return itemIdArray[1] > databilityIndex
                            ? {
                                  ...limit,
                                  itemId: `${itemIdArray[0]}-${itemIdArray[1] - 1}`,
                              }
                            : limit;
                    });

                prev.databilities = prev.databilities.filter(({ id }) => id !== databilityId);
            })
        );
    };

    return (
        <FormWrapper>
            <Carousel label='Data groups' addItem={onAddDataGroup}>
                {viewblockState.settings.headings.map((heading, headingIndex) => (
                    <React.Fragment key={heading.id}>
                        <Text label='Group Label' value={heading.label} onChange={onHeadingChange(headingIndex, 'label')} />
                        <Carousel label='Data' addItem={() => onAddHeading(headingIndex)}>
                            {heading.databilities.map((id, i) => (
                                <DatabilityItem
                                    key={id}
                                    databilityId={id}
                                    databilityIndex={i}
                                    heading={heading}
                                    headingIndex={headingIndex}
                                    viewblockState={viewblockState}
                                    setViewblockState={setViewblockState}
                                    onDelete={() => onDeleteHeading(headingIndex, i)}
                                />
                            ))}
                        </Carousel>
                        <div style={{ gridColumn: '1 / 6', gridRow: 'auto', textAlign: 'center' }}>
                            <hr style={{ marginTop: 0 }} />
                            <Button onClick={() => onDeleteDataGroup(heading.id)} danger icon={<DeleteOutlined />}>
                                Delete&nbsp;<i>{heading.label}</i>
                            </Button>
                        </div>
                    </React.Fragment>
                ))}
            </Carousel>
        </FormWrapper>
    );
};

export default DatabilitiesConfigurator;
